import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Package, Plus, Search, RefreshCw, Trash2, X, Save, ArrowUp, ArrowDown, Recycle, ChevronDown, ChevronUp, Calendar, Truck } from 'lucide-react';

function FerrousScrap() {
  const { API, user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState([]);
  const [totals, setTotals] = useState({ totalStock: 0, totalDispatched: 0, totalAvailable: 0 });
  const [entries, setEntries] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState(true);
  const [showDispatches, setShowDispatches] = useState(false);
  
  // Add Entry Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    scrapType: 'STEEL SCRAP',
    quantity: '',
    source: '',
    date: new Date().toISOString().split('T')[0],
    rate: '',
    amount: '',
    gstPercent: '18',
    totalValue: '',
    grade: '',
    remarks: ''
  });

  const scrapTypes = [
    'STEEL SCRAP',
    'IRON SCRAP',
    'HMS 1',
    'HMS 2',
    'SHREDDED SCRAP',
    'PLATE & STRUCTURAL',
    'TURNINGS'
  ];

  useEffect(() => {
    fetchStockData();
  }, [API]);

  const fetchStockData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/raw-material/ferrous-stock`);
      setStockData(response.data.summary || []);
      setTotals(response.data.totals || { totalStock: 0, totalDispatched: 0, totalAvailable: 0 });
      setEntries(response.data.entries || []);
      setDispatches(response.data.dispatches || []);
    } catch (error) {
      console.error('Error fetching ferrous stock:', error);
      const defaultData = scrapTypes.map(type => ({
        id: type,
        scrapType: type,
        totalAdded: 0,
        totalDispatched: 0,
        available: 0,
        entries: 0
      }));
      setStockData(defaultData);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate amount when rate or quantity changes
      if (name === 'rate' || name === 'quantity') {
        const rate = name === 'rate' ? (parseFloat(value) || 0) : (parseFloat(prev.rate) || 0);
        const qty = name === 'quantity' ? (parseFloat(value) || 0) : (parseFloat(prev.quantity) || 0);
        if (rate > 0 && qty > 0) {
          const baseAmount = rate * qty;
          updated.amount = baseAmount.toFixed(2);
          const gst = parseFloat(prev.gstPercent) || 0;
          updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
        }
      }
      
      // Auto-calculate total value when amount or GST changes
      if (name === 'amount' || name === 'gstPercent') {
        const baseAmount = name === 'amount' ? (parseFloat(value) || 0) : (parseFloat(prev.amount) || 0);
        const gst = name === 'gstPercent' ? (parseFloat(value) || 0) : (parseFloat(prev.gstPercent) || 0);
        updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/raw-material/ferrous-stock`, {
        scrapType: formData.scrapType,
        quantity: parseFloat(formData.quantity),
        source: formData.source || 'Manual Entry',
        date: formData.date,
        rate: formData.rate ? parseFloat(formData.rate) : 0,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        gstPercent: formData.gstPercent ? parseFloat(formData.gstPercent) : 0,
        totalValue: formData.totalValue ? parseFloat(formData.totalValue) : 0,
        grade: formData.grade,
        remarks: formData.remarks
      });
      
      setShowAddModal(false);
      setFormData({
        scrapType: 'STEEL SCRAP',
        quantity: '',
        source: '',
        date: new Date().toISOString().split('T')[0],
        rate: '',
        amount: '',
        gstPercent: '18',
        totalValue: '',
        grade: '',
        remarks: ''
      });
      fetchStockData();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert(error.response?.data?.detail || 'Error adding entry');
    }
    setSaving(false);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this stock entry?')) return;
    try {
      await axios.delete(`${API}/raw-material/ferrous-stock/${entryId}`);
      fetchStockData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry');
    }
  };


  const getTypeColor = (type) => {
    if (type?.includes('STEEL')) return 'bg-gray-200 text-gray-800';
    if (type?.includes('IRON')) return 'bg-slate-200 text-slate-800';
    if (type?.includes('HMS 1')) return 'bg-blue-100 text-blue-700';
    if (type?.includes('HMS 2')) return 'bg-indigo-100 text-indigo-700';
    if (type?.includes('SHREDDED')) return 'bg-purple-100 text-purple-700';
    if (type?.includes('PLATE')) return 'bg-teal-100 text-teal-700';
    if (type?.includes('TURNINGS')) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getCardGradient = (type) => {
    if (type?.includes('STEEL')) return 'from-gray-600 to-gray-700';
    if (type?.includes('IRON')) return 'from-slate-500 to-slate-600';
    return 'from-gray-500 to-gray-600';
  };

  const filteredData = stockData.filter(item => 
    item.scrapType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate summary for cards
  const steelStock = stockData.find(s => s.scrapType === 'STEEL SCRAP')?.available || 0;
  const ironStock = stockData.find(s => s.scrapType === 'IRON SCRAP')?.available || 0;
  const hmsStock = stockData.filter(s => s.scrapType?.includes('HMS')).reduce((sum, s) => sum + (s.available || 0), 0);

  return (
    <div className="space-y-4" data-testid="ferrous-scrap-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg">
            <Recycle className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Ferrous Scrap Stock</h1>
            <p className="text-sm text-gray-500">Iron & Steel Scrap Inventory Management</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-32"
              data-testid="search-input"
            />
          </div>
          <button 
            onClick={fetchStockData}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Refresh"
            data-testid="refresh-btn"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition text-sm font-medium"
            data-testid="add-entry-btn"
          >
            <Plus size={16} />
            Add Stock Entry
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Steel Scrap</p>
          <p className="text-2xl font-bold">{steelStock.toLocaleString()} kg</p>
        </div>
        <div className="bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Iron Scrap</p>
          <p className="text-2xl font-bold">{ironStock.toLocaleString()} kg</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">HMS (1 & 2)</p>
          <p className="text-2xl font-bold">{hmsStock.toLocaleString()} kg</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Available</p>
          <p className="text-2xl font-bold">{totals.totalAvailable?.toLocaleString() || 0} kg</p>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="stock-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Scrap Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 uppercase">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowUp size={14} />
                    Total Added (kg)
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowDown size={14} />
                    Dispatched (kg)
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase">Available Stock</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No stock data found. Add entries to start tracking.
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition" data-testid={`stock-row-${item.scrapType}`}>
                    <td className="px-4 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(item.scrapType)}`}>
                        {item.scrapType}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium text-green-600">+{(item.totalAdded || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium text-red-600">-{(item.totalDispatched || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-xl font-bold text-blue-600">{(item.available || 0).toLocaleString()} kg</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                        {item.entries || 0}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock Entries Detail */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="stock-entries-section">
        <button
          onClick={() => setShowEntries(!showEntries)}
          className="w-full px-4 py-3 bg-green-50 border-b border-green-200 flex items-center justify-between hover:bg-green-100 transition"
        >
          <h3 className="font-semibold text-green-800 flex items-center gap-2">
            <ArrowUp size={16} className="text-green-600" />
            Stock Entries ({entries.length})
          </h3>
          {showEntries ? <ChevronUp size={18} className="text-green-600" /> : <ChevronDown size={18} className="text-green-600" />}
        </button>
        {showEntries && (
          <div className="overflow-x-auto">
            {entries.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">No stock entries yet. Click "Add Stock Entry" to start.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Scrap Type</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Qty (kg)</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Source</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Grade</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Rate (₹/kg)</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Amount (₹)</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Remarks</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700">{entry.date || '-'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(entry.scrapType)}`}>
                          {entry.scrapType}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-green-600">+{(entry.quantity || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-600">{entry.source || '-'}</td>
                      <td className="px-4 py-2 text-gray-600">{entry.grade || '-'}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{entry.rate ? `₹${parseFloat(entry.rate).toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{entry.totalValue ? `₹${parseFloat(entry.totalValue).toLocaleString()}` : entry.amount ? `₹${parseFloat(entry.amount).toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs max-w-[150px] truncate">{entry.remarks || '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition"
                          title="Delete entry"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Dispatch Records Detail */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="dispatch-records-section">
        <button
          onClick={() => setShowDispatches(!showDispatches)}
          className="w-full px-4 py-3 bg-red-50 border-b border-red-200 flex items-center justify-between hover:bg-red-100 transition"
        >
          <h3 className="font-semibold text-red-800 flex items-center gap-2">
            <Truck size={16} className="text-red-600" />
            Dispatch Deductions ({dispatches.length})
          </h3>
          {showDispatches ? <ChevronUp size={18} className="text-red-600" /> : <ChevronDown size={18} className="text-red-600" />}
        </button>
        {showDispatches && (
          <div className="overflow-x-auto">
            {dispatches.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">No dispatch deductions yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Scrap Type</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Qty (kg)</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Qty (MT)</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Customer</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Dispatch ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dispatches.map((d, idx) => (
                    <tr key={d.dispatchId || idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700">{d.date || '-'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(d.scrapType)}`}>
                          {d.scrapType}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-red-600">-{(d.quantity || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{d.quantityMT ? `${d.quantityMT} MT` : `${((d.quantity || 0) / 1000).toFixed(2)} MT`}</td>
                      <td className="px-4 py-2 text-gray-600">{d.customer || '-'}</td>
                      <td className="px-4 py-2 text-gray-400 text-xs">{d.dispatchId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
        <h4 className="font-semibold mb-2">Stock Calculation:</h4>
        <p className="text-xs">
          <strong>Available Stock = Total Added - Dispatched</strong><br/>
          <span className="text-green-600 font-medium">Added</span>: Stock entries added manually via this page<br/>
          <span className="text-red-600 font-medium">Dispatched</span>: Automatically deducted when Steel/Iron Scrap is sold via Dispatch Records
        </p>
      </div>

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Ferrous Stock Entry</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddEntry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scrap Type *</label>
                <select
                  name="scrapType"
                  value={formData.scrapType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  required
                  data-testid="scrap-type-select"
                >
                  {scrapTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (kg) *</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  placeholder="e.g., 5000"
                  step="any"
                  min="0"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  required
                  data-testid="quantity-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate (₹/kg)</label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    placeholder="e.g., 50"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  />
                </div>
              </div>

              {/* Amount, GST & Total Value */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Before GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
                  <input
                    type="number"
                    name="gstPercent"
                    value={formData.gstPercent}
                    onChange={handleInputChange}
                    placeholder="18"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">Total Value (₹)</label>
                  <input
                    type="number"
                    name="totalValue"
                    value={formData.totalValue}
                    onChange={handleInputChange}
                    placeholder="With GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border-2 border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-green-50 font-semibold text-green-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    placeholder="e.g., Local Collection"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <input
                    type="text"
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                    placeholder="e.g., Grade A"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  placeholder="Optional notes..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
                  data-testid="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-btn"
                >
                  {saving ? (
                    <><RefreshCw className="animate-spin" size={16} />Saving...</>
                  ) : (
                    <><Save size={16} />Add Entry</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FerrousScrap;
